import React, { useState, useEffect, useMemo } from 'react'; // <--- Agrega useMemo
import { Link, useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useAuth } from '../../context/AuthContext';
import NameModal from '../../components/modals/NameModal';
import meditationImage from '../../assets/meditation/meditation.png';
import gammaImage from '../../assets/gamma/gamma.png';

// Import react-icons
import { FaInstagram, FaYoutube } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6"; 
import perfilImg from '../../assets/images/perfil.png';
import merkabaImg from '../../assets/images/merkaba/Merkaba.png';
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';

// Función auxiliar para obtener una imagen aleatoria
const getRandomCardImage = () => `https://gusaguiar.com/assets/oracle/deck-en/${Math.floor(Math.random() * 89) + 1}.png`;

// --- CONFIGURACIÓN DEL MENÚ ---
const menuItems = [
  {
    title: "Oráculo Metafísico",
    subtitle: "Opciones del Oráculo para tu viaje",
    cardCount: 5,
    path: "/oraculo/menu",
    featured: true,
  },
  {
    title: "Meditación de 7 Fases",
    subtitle: "Entra en estados elevados con esta meditación guiada",
    image: meditationImage,
    path: "/sietefases?lg=es",
    featured: true,
  },
  {
    title: "Respiración Wim Hof",
    subtitle: "Activa tu sistema nervioso y eleva tu energía",
    image: gammaImage,
    path: "/wimhof?lg=es",
    featured: true,
  },
];

// Componente optimizado: Imágenes estáticas y dimensiones conservadas
const CardStack = ({ count }) => {
  // USAMOS useMemo: Solo genera las imágenes una vez al inicio.
  // No cambiarán aunque la página se renderice por las animaciones.
  const cards = useMemo(() => {
    return [...Array(count)].map(() => getRandomCardImage());
  }, [count]);

  return (
    <div className="relative h-full flex items-center justify-center w-full">
      {cards.map((imgSrc, i) => {
        // Lógica del abanico
        const offset = i - Math.floor(count / 2);
        
        // Ajustes visuales para el abanico
        const rotation = offset * 8; 
        const translateX = offset * 14; 
        const translateY = Math.abs(offset) * 2; 
        const scale = 1 - (Math.abs(offset) * 0.02); 

        return (
          <img
            key={i}
            src={imgSrc}
            alt={`Carta ${i}`}
            className="absolute h-full w-auto object-contain rounded-md shadow-xl border border-gray-600/60 bg-gray-900 transition-transform duration-300 group-hover:scale-110"
            style={{
              zIndex: i, 
              transformOrigin: '50% 90%', // Pivote abajo para efecto abanico real
              transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg) scale(${scale})`,
              left: 0,
              right: 0,
              margin: '0 auto' // Centrado absoluto
            }}
          />
        );
      })}
    </div>
  );
};
const LinktreeButton = ({ item, index, onButtonClick }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const isFeatured = item.featured;
  const linkBaseClasses = `group relative w-full max-w-md mx-auto mb-4 flex items-center justify-center rounded-xl text-white font-medium transition-all duration-300 ease-in-out ${inView ? "animate-fade-in" : "opacity-0 translate-y-4"} border border-gray-700 shadow-md shadow-gray-700/30`;
  const contentDivClasses = `relative w-full flex items-center rounded-xl transition-colors duration-300 ${isFeatured ? 'flex-col py-4 px-5 bg-black/50 hover:bg-black/70' : 'flex-row py-3 px-4 bg-black/30 hover:bg-gray-800'}`;

  return (
    <Link to={item.path} ref={ref} className={linkBaseClasses} style={{ animationDelay: `${index * 100}ms` }} onClick={() => onButtonClick(item.title, item.path)}>
      <div className={contentDivClasses}>
        
        {/* LOGICA DE IMAGEN O MAZO */}
        <div className={`flex-shrink-0 flex items-center justify-center ${isFeatured ? 'w-40 h-40 mb-4' : 'w-10 h-10 mr-4'}`}>
          {item.cardCount && item.cardCount > 1 ? (
            // Si tiene cardCount > 1, mostramos el mazo solapado
            <CardStack count={item.cardCount} />
          ) : (
            // Si no, mostramos la imagen única normal
            item.image && (
              <img src={item.image} alt="Icono" className="w-full h-full object-contain rounded-md shadow-md" />
            )
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center z-10">
          {item.title && <span className={`font-semibold text-base sm:text-lg ${isFeatured ? 'mt-2' : ''} text-gray-100 group-hover:text-white transition-colors`}>{item.title}</span>}
          {item.subtitle && <span className={`text-sm text-gray-400 mt-1 ${isFeatured ? 'text-base' : ''} group-hover:text-gray-300 transition-colors`}>{item.subtitle}</span>}
        </div>
        
        {/* Spacer para equilibrar diseño no destacado */}
        {!isFeatured && (item.image || item.cardCount) && (
          <div className="w-10 h-10" aria-hidden="true"></div>
        )}
      </div>
    </Link>
  );
};


const HomePageEs = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showNameModal, setShowNameModal] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [merkabaOpacity, setMerkabaOpacity] = useState(0);
  const [merkabaRotation, setMerkabaRotation] = useState(0);
  const [titleGlowIntensity, setTitleGlowIntensity] = useState(0.2);

  const displayName = user?.name || guestName;

  useEffect(() => {
    const savedGuestName = localStorage.getItem('track_user_name');
    if (savedGuestName) {
      setGuestName(savedGuestName);
    }
    const nameModalDismissed = sessionStorage.getItem('nameModalDismissed');
    if (!user && !nameModalDismissed && !savedGuestName) {
      const timer = setTimeout(() => setShowNameModal(false), 7000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    let currentRotation = 0;
    const animateMerkaba = () => {
      currentRotation = (currentRotation + 30) % 360;
      setMerkabaRotation(currentRotation); setMerkabaOpacity(0.5);
      const fadeOutTimer = setTimeout(() => setMerkabaOpacity(0), 2000);
      const loopTimer = setTimeout(animateMerkaba, 3500);
      return () => { clearTimeout(fadeOutTimer); clearTimeout(loopTimer); };
    };
    const initialDelay = setTimeout(animateMerkaba, 500);
    return () => clearTimeout(initialDelay);
  }, []);

  useEffect(() => {
    const animateGlow = () => setTitleGlowIntensity(prev => (prev === 0.2 ? 0.4 : 0.2));
    const glowInterval = setInterval(animateGlow, 3000);
    return () => clearInterval(glowInterval);
  }, []);

  const handleNameSubmit = (name) => {
    localStorage.setItem('track_user_name', name);
    setGuestName(name);
    setShowNameModal(false);
    sendCodedLog({ message: 'Invitado añadió su nombre', event: 'guest_name_set_es', ...Common.getCommonLogData(location) });
  };

  const handleModalClose = () => {
    sessionStorage.setItem('nameModalDismissed', 'true');
    setShowNameModal(false);
    sendCodedLog({ message: 'Invitado cerró el modal de nombre', event: 'guest_modal_name_dismiss_es', ...Common.getCommonLogData(location) });
  };

  const handleLinktreeButtonClick = (title, path) => {
    sendCodedLog({ message: `Click en botón "${title}" (${path})`, event: 'home_oraculo_portal_button_click_es', ...Common.getCommonLogData(location) });
  };
  
  const handleProfileImageClick = () => sendCodedLog({ message: 'Click en imagen de perfil', event: 'home_profile_image_click_es', ...Common.getCommonLogData(location) });
  
  const handleSocialMediaClick = (platform, url) => {
    sendCodedLog({ message: `Click en red social (Home): ${platform}`, event: `home_social_media_click_${platform.toLowerCase()}_es`, ...Common.getCommonLogData(location) });
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  const handleInstagramClick = (e) => { e.preventDefault(); handleSocialMediaClick('Instagram', 'https://instagram.com/quantum.gus'); };
  const handleTiktokClick = (e) => { e.preventDefault(); handleSocialMediaClick('TikTok', 'https://www.tiktok.com/@quantum.gus'); };
  const handleYoutubeClick = (e) => { e.preventDefault(); handleSocialMediaClick('YouTube', 'https://www.youtube.com/@imanifesti'); };

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-neutral-900 to-black"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">

        <div className="relative flex items-center justify-between mb-8 w-full max-w-2xl mx-auto">
          <h1 className="relative z-10 text-3xl sm:text-6xl font-bold leading-none text-left mt-7 mb-7" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
            Portal
          </h1>
          <img src={merkabaImg} alt="Merkaba Dorado" className="absolute z-0 w-36 h-36 sm:w-44 sm:h-44 object-contain rounded-full transition-all duration-1000 ease-in-out" style={{ opacity: merkabaOpacity, top: '50%', left: '50%', transform: `translate(-50%, -50%) rotate(${merkabaRotation}deg)` }} />
          <h1 className="relative z-10 text-3xl sm:text-6xl font-bold leading-none text-right mt-7 mb-7" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
            Oracle
          </h1>
        </div>

        <div className="text-center mb-10">
            <div className="relative flex items-baseline justify-center gap-2">
                <h2 className="relative z-10 text-2xl sm:text-2xl font-bold leading-none" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #c4c1b3ff, #e4d4adff, #e7d9b6ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Hola,
                </h2>
            </div>
            <h1 className="relative z-10 text-3xl sm:text-4xl font-bold leading-none mt-2" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #c4c1b3ff, #e4d4adff, #e7d9b6ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
                {displayName || 'Viajero'}
            </h1>
            <p className="text-sm sm:text-base text-gray-400 mt-3 max-w-sm mx-auto">
                {user
                    ? "Continúa tu viaje de transformación."
                    : "Explora herramientas para tu crecimiento personal"
                }
            </p>
        </div>
        
        <div className="w-full flex flex-col items-center">
          {menuItems.map((item, index) => (
            <LinktreeButton key={item.path + index} item={item} index={index} onButtonClick={handleLinktreeButtonClick} />
          ))}
        </div>

        {!displayName && (
            <div className="flex flex-col items-center mt-12 mb-8">
                <img
                    src={perfilImg}
                    alt="Foto de perfil" 
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full mb-2 object-cover border-4 border-gray-700 shadow-lg cursor-pointer"
                    onClick={handleProfileImageClick}
                />
                <h2 className="text-xl sm:text-2xl font-bold mb-0.5 text-white">
                    Gustavo • Aguiar
                </h2>
                <p className="text-sm sm:text-base text-gray-400 text-center max-w-sm mb-8">
                    Coach Metafísico <br/> Desbloquea tu estado mental y entra en estados del ser elevados.
                </p>
            </div>
        )}

        <div className="flex justify-center gap-6 text-2xl text-gray-300 mt-8">
          <a onClick={handleInstagramClick} aria-label="Instagram" className="hover:text-white cursor-pointer"><FaInstagram /></a>
          {/* <a onClick={handleTiktokClick} aria-label="TikTok" className="hover:text-white cursor-pointer"><FaTiktok /></a> */}
          <a onClick={handleYoutubeClick} aria-label="YouTube" className="hover:text-white cursor-pointer"><FaYoutube /></a>
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        <p>© {new Date().getFullYear()} Gus • Aguiar | En la misión de elevar y transformar.</p>
      </footer>

      {showNameModal && <NameModal onClose={handleModalClose} onSubmitName={handleNameSubmit} />}
    </div>
  );
};

export default HomePageEs;